import {
  Children,
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import styled from "styled-components";

/**
 * 스크롤 가능한 목록의 위/아래 가장자리에 옅은 회색 그라데이션 페이드를 덧대,
 * "위/아래에 더 많은 콘텐츠가 있음"을 알려주는 공용 래퍼.
 *
 * - 자식으로 스크롤 컨테이너(overflow-y: auto 등) 하나만 넘긴다.
 *   해당 자식의 스타일(높이·배경·스크롤바 등)은 그대로 유지되고,
 *   ScrollFade는 ref·onScroll만 얹어 스크롤 위치를 감지한다.
 * - 위로 스크롤 여지가 있으면 상단, 아래로 여지가 있으면 하단 페이드를 보여준다.
 * - 페이드는 pointer-events: none 이라 목록 클릭을 막지 않는다.
 *
 * @param size  페이드 높이(px). 기본 28
 * @param color 페이드 시작 색. 기본 rgba(0,0,0,0.1)(옅은 회색)
 */
const Wrapper = styled.div`
  position: relative;
`;

const Fade = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: ${({ $size }) => $size}px;
  pointer-events: none;
  z-index: 1;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.15s ease;
  ${({ $edge, $color }) =>
    $edge === "top"
      ? `top: 0; background: linear-gradient(to bottom, ${$color} 0%, transparent 100%);`
      : `bottom: 0; background: linear-gradient(to top, ${$color} 0%, transparent 100%);`}
`;

export function ScrollFade({
  children,
  size = 28,
  // 옅은 회색이 살짝 도는 페이드. 같은 색(검정)의 알파만 0으로 빠지게 해
  // 중간에 색이 변하는 띠가 생기지 않는다.
  color = "rgba(0, 0, 0, 0.1)",
  className,
  style,
}) {
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const update = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const top = scrollTop > 1;
    const bottom = scrollHeight - scrollTop - clientHeight > 1;
    setEdges((prev) =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
    );
  }, []);

  // 자식(목록)이 바뀌어 높이가 달라지는 경우까지 매 렌더 후 재계산한다.
  useLayoutEffect(update);

  // 컨테이너 크기 변화(반응형·창 리사이즈)에도 대응한다.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [update]);

  const scroller = Children.only(children);
  const handleScroll = (event) => {
    update();
    scroller.props.onScroll?.(event);
  };

  return (
    <Wrapper className={className} style={style}>
      <Fade $edge="top" $size={size} $color={color} $visible={edges.top} />
      {cloneElement(scroller, { ref: scrollerRef, onScroll: handleScroll })}
      <Fade
        $edge="bottom"
        $size={size}
        $color={color}
        $visible={edges.bottom}
      />
    </Wrapper>
  );
}

export default ScrollFade;
